/* eslint-disable max-lines */
import { Button } from '@heidi/design-system/button';
import { SearchInput } from '@heidi/design-system/search-input';
import {
  TypographyCaption,
  TypographyP2,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { getFaviconUrl } from '@heidi/utils';
import { BookOpen, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FaviconImage } from '../FaviconImage';
import { LibrarySourceUrlOptin } from '../LibrarySourceUrlOptin';
import { SourceSearchInput } from '../source-lists/SourceSearchInput';
import { useDomainDisplayNames } from '../source-lists/useDomainDisplayNames';
import { useLibrarySourceUrlRiskGate } from '../useLibrarySourceUrlRiskGate';
import { InlineEditableName } from './InlineEditableName';
import { TeamToggleContent, type TeamToggleStepProps } from './TeamCollectionSwitch';
import { WizardStepFooter } from './WizardStepFooter';

/**
 * Filter + render the list of domains already added to the collection.
 *
 * Pulled out so the two `WebSourcesStep` variants share the same affordance.
 * The filter input is purely a client-side narrow against `addedDomains` —
 * it has no overlap with `SourceSearchInput`, which is the *discovery*
 * surface for sources NOT yet in the collection. Splitting them addresses
 * the confusion where users searched for an already-added source in the
 * top "Search to add more sources" bar, hit the `excludedDomains` filter,
 * and saw "no results".
 */
const AddedSourcesList = ({
  addedDomains,
  onRemoveDomain,
  maxDomains,
}: {
  addedDomains: string[];
  onRemoveDomain: (domain: string) => void;
  /** When provided, the counter renders as `count / maxDomains` so the user
   *  sees the per-collection ceiling. Same cap the BE enforces. */
  maxDomains?: number;
}) => {
  const intl = useIntl();
  const { getDomainLabel } = useDomainDisplayNames();
  const [filterQuery, setFilterQuery] = useState('');

  const visibleDomains = useMemo(() => {
    const q = filterQuery.trim().toLowerCase();
    if (!q) return addedDomains;
    return addedDomains.filter(
      (d) => d.toLowerCase().includes(q) || getDomainLabel(d).toLowerCase().includes(q)
    );
  }, [addedDomains, filterQuery, getDomainLabel]);

  if (addedDomains.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4">
        <BookOpen size={24} className="text-text-tertiary" />
        <TypographyCaption className="text-text-tertiary">
          <FormattedMessage
            defaultMessage="No sources yet. Search to add your preferred sources."
            id="3riZAtKM1h"
            description="Empty state when no web sources have been added to the collection in the create-collection wizard"
          />
        </TypographyCaption>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex shrink-0 items-center justify-between gap-3">
        <TypographyPBold>
          <FormattedMessage
            defaultMessage="Sources in this collection"
            id="Pg++NYtWvj"
            description="Heading above the list of web sources already added to the collection"
          />
        </TypographyPBold>
        <TypographyCaption
          className="text-text-tertiary"
          data-testid="added-sources-counter"
        >
          {maxDomains !== undefined
            ? intl.formatMessage(
                {
                  defaultMessage: '{count, number} / {max, number}',
                  id: 'SfUu5wE6HN',
                  description:
                    'Counter showing how many sources are in the collection versus the per-collection maximum',
                },
                { count: addedDomains.length, max: maxDomains }
              )
            : addedDomains.length}
        </TypographyCaption>
      </div>
      <SearchInput
        placeholder={intl.formatMessage({
          defaultMessage: 'Search sources in this collection',
          id: 'hNg1/CzCgH',
          description:
            'Placeholder for filtering web sources already added to the collection',
        })}
        clearAriaLabel={intl.formatMessage({
          defaultMessage: 'Clear search',
          id: '4YJHutR26p',
        })}
        value={filterQuery}
        onChange={(e) => setFilterQuery(e.target.value)}
        wrapperClassName="shrink-0"
        data-testid="added-sources-filter"
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        {visibleDomains.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-4 py-6">
            <TypographyCaption className="text-text-tertiary">
              <FormattedMessage
                defaultMessage="No sources in this collection match your search."
                id="4y4qLv0i8G"
                description="Message when the in-collection source filter has no matching results"
              />
            </TypographyCaption>
          </div>
        ) : (
          visibleDomains.map((domain) => (
            <div key={domain} className="flex items-start gap-3 px-1 py-2.5">
              <div className="flex size-6 shrink-0 items-center justify-center">
                <FaviconImage src={getFaviconUrl(`https://${domain}`)} />
              </div>
              <div className="min-w-0 flex-1">
                <TypographyP2 className="truncate">{getDomainLabel(domain)}</TypographyP2>
                <TypographyCaption className="truncate text-text-tertiary">
                  {domain}
                </TypographyCaption>
              </div>
              <Button
                type="button"
                variant="destructive_ghost"
                size="xs"
                iconOnly
                className="shrink-0"
                onClick={() => onRemoveDomain(domain)}
                aria-label={intl.formatMessage(
                  {
                    defaultMessage: 'Remove {domain}',
                    id: 'A5t2p+7bAs',
                    description:
                      'Accessibility label for removing a web source from the collection',
                  },
                  { domain }
                )}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/**
 * Content-only variant for use in unified create collection modal (no header/footer).
 */
export const WebSourcesStepContent = ({
  addedDomains,
  onAddDomain,
  onAddDomains,
  onRemoveDomain,
  maxDomains,
}: {
  addedDomains: string[];
  onAddDomain: (domain: string) => void;
  onAddDomains?: (domains: string[]) => void;
  onRemoveDomain: (domain: string) => void;
  /** Optional per-collection cap surfaced as a `count / max` counter. */
  maxDomains?: number;
}) => {
  const intl = useIntl();
  const riskGate = useLibrarySourceUrlRiskGate();

  if (riskGate.needsOptin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <LibrarySourceUrlOptin
          accepted={riskGate.checkboxAccepted}
          onAcceptedChange={riskGate.setCheckboxAccepted}
          onConfirm={riskGate.confirm}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Discovery surface anchored at the top so its dropdown panel
          (absolute, ``top-full``) opens DOWN into the visible viewport
          instead of off-screen — a tall pre-seeded list (e.g. 300
          recommendation domains) used to push the input below the fold
          and the results panel was clipped. The curated list moves
          below and keeps growing/scrolling internally. */}
      <div className="flex shrink-0 flex-col gap-2">
        <TypographyPBold>
          <FormattedMessage
            defaultMessage="Add more sources"
            id="ELWfzsY+el"
            description="Heading above the search input for discovering new web sources to add to the collection"
          />
        </TypographyPBold>
        <SourceSearchInput
          excludedDomains={addedDomains}
          onSelectSource={onAddDomain}
          onSelectSources={onAddDomains}
          maxAllowed={maxDomains}
          placeholder={intl.formatMessage({
            defaultMessage: 'Search for additional sources here',
            id: 'qL2WmKjF6+',
            description:
              'Placeholder for the discovery search input that adds new web sources to the collection',
          })}
          autoFocus={false}
        />
      </div>
      <AddedSourcesList
        addedDomains={addedDomains}
        onRemoveDomain={onRemoveDomain}
        maxDomains={maxDomains}
      />
    </div>
  );
};

interface WebSourcesStepProps extends TeamToggleStepProps {
  name: string;
  onNameChange: (name: string) => void;
  addedDomains: string[];
  onAddDomain: (domain: string) => void;
  onAddDomains?: (domains: string[]) => void;
  onRemoveDomain: (domain: string) => void;
  onBack?: () => void;
  onCancel: () => void;
  onSave: () => void;
  isSaving: boolean;
  error: string | null;
  /** Override the default "Save" button label. */
  saveLabel?: string;
  /** Optional per-collection cap surfaced as a `count / max` counter. */
  maxDomains?: number;
}

/**
 * Step 2A of the Create Collection Wizard.
 * Shows the search input for adding more web sources and a growing list
 * of already-added sources with remove buttons.
 */
export const WebSourcesStep = ({
  name,
  onNameChange,
  addedDomains,
  onAddDomain,
  onAddDomains,
  onRemoveDomain,
  isTeamCollection,
  onTeamCollectionChange,
  hasTeam,
  canCreateTeamCollection,
  teamName,
  onBack,
  onCancel,
  onSave,
  isSaving,
  error,
  saveLabel,
  maxDomains,
}: WebSourcesStepProps) => {
  const intl = useIntl();
  const riskGate = useLibrarySourceUrlRiskGate();

  // Gate the whole step behind the URL-source risk acknowledgement, mirroring
  // the unified `WebSourcesStepContent`. The name header stays mounted for
  // visual continuity with the next step; Cancel backs out of the wizard
  // entirely (there is no curated content to preserve yet).
  if (riskGate.needsOptin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div className="flex items-center gap-2">
          <InlineEditableName value={name} onChange={onNameChange} className="flex-1" />
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <LibrarySourceUrlOptin
            accepted={riskGate.checkboxAccepted}
            onAcceptedChange={riskGate.setCheckboxAccepted}
            onConfirm={riskGate.confirm}
            onCancel={onCancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      {/* Header with inline editable name */}
      <div className="flex items-center gap-2">
        <InlineEditableName value={name} onChange={onNameChange} className="flex-1" />
      </div>

      {/* Search + results group. The discovery surface sits at the top
          so its absolute-positioned dropdown opens DOWN into the visible
          area; a 300-item pre-seeded list (recommendation banner →
          Customise path) otherwise pushed the input off-screen and
          clipped the dropdown. The curated list keeps `flex-1 min-h-0`
          so it owns the remaining vertical space and scrolls internally. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {/* Discovery surface — adds NEW sources. autoFocus is intentional:
            the discovery input is the primary action on this step for
            both empty and populated collections. */}
        <div className="flex shrink-0 flex-col gap-2">
          <TypographyPBold>
            <FormattedMessage
              defaultMessage="Add more sources"
              id="ELWfzsY+el"
              description="Heading above the search input for discovering new web sources to add to the collection"
            />
          </TypographyPBold>
          <SourceSearchInput
            excludedDomains={addedDomains}
            onSelectSource={onAddDomain}
            onSelectSources={onAddDomains}
            maxAllowed={maxDomains}
            placeholder={intl.formatMessage({
              defaultMessage: 'Search for additional sources here',
              id: 'qL2WmKjF6+',
              description:
                'Placeholder for the discovery search input that adds new web sources to the collection',
            })}
            autoFocus
          />
        </div>

        {/* In-collection filter + list — narrows what is already curated. */}
        <AddedSourcesList
          addedDomains={addedDomains}
          onRemoveDomain={onRemoveDomain}
          maxDomains={maxDomains}
        />
      </div>

      {/* Footer */}
      <WizardStepFooter
        onBack={onBack}
        onCancel={onCancel}
        onSave={onSave}
        isSaving={isSaving}
        isSaveDisabled={addedDomains.length === 0}
        error={error}
        saveLabel={saveLabel}
        leftContent={
          <TeamToggleContent
            isTeamCollection={isTeamCollection}
            onTeamCollectionChange={onTeamCollectionChange}
            hasTeam={hasTeam}
            canCreateTeamCollection={canCreateTeamCollection}
            teamName={teamName}
          />
        }
      />
    </div>
  );
};
